import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Loader2, Plus, Users, X } from "lucide-react";
import { toast } from "sonner";
import type { Tier } from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/shared/empty-state";
import { TierBadge } from "@/components/shared/tier-badge";
import { formatApiError } from "@/lib/format-api-error";
import { cn, formatRelative } from "@/lib/utils";
import { usePartners } from "@/features/partners/use-partners";
import { useAssignPartnerTier } from "./use-assign-partner-tier";
import {
  useAssignmentsForProgram,
  type AdminTierAssignmentRow,
} from "./use-assignments-for-program";
import { useUnassignPartnerTier } from "./use-unassign-partner-tier";

// Phase 7 sub-bucket 12 — partner-tier assignments tab.
//
// Replaces legacy app/admin/tiers/[id]/_components/TierAssignmentsTab.tsx
// with a combobox-driven partner picker (over the existing usePartners
// list) instead of the scrollable button list. Per-assign / per-unassign
// mutations match legacy.
//
// The assign mutation passes `patchPartnerLegacyTier` to opt into the
// optimistic partners.tier patch when (a) the program is the default and
// (b) the picked tier carries a `legacy_tier` slug. Outside that window the
// hook still does the cross-key invalidate so partner caches converge on
// the server's response.

export function TierAssignmentsTab({
  programId,
  tiers,
}: {
  programId: string;
  tiers: Tier[];
}) {
  const assignmentsQuery = useAssignmentsForProgram(programId);
  const assignments = useMemo(
    () => assignmentsQuery.data ?? [],
    [assignmentsQuery.data],
  );
  const [assignOpen, setAssignOpen] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState<{
    partnerId: string;
    partnerName: string;
  } | null>(null);

  const unassign = useUnassignPartnerTier();

  function handleConfirmUnassign() {
    if (!unassignTarget) return;
    unassign.mutate(
      { programId, partnerId: unassignTarget.partnerId },
      {
        onSuccess: () => {
          toast.success("Assignment removed");
          setUnassignTarget(null);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to remove assignment"));
          setUnassignTarget(null);
        },
      },
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Partner assignments
          </h2>
          <p className="text-sm text-muted-foreground">
            {assignments.length}{" "}
            {assignments.length === 1 ? "partner" : "partners"} assigned
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => setAssignOpen(true)}
          disabled={tiers.length === 0}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Assign partner
        </Button>
      </div>

      {assignmentsQuery.isLoading ? (
        <EmptyState
          title="Loading assignments…"
          description="One moment."
        />
      ) : assignments.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No partners assigned yet"
          description={
            tiers.length === 0
              ? "Add at least one tier before assigning partners."
              : "Pick a partner and a tier to begin."
          }
          action={
            tiers.length > 0 ? (
              <Button size="sm" onClick={() => setAssignOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Assign first partner
              </Button>
            ) : undefined
          }
        />
      ) : (
        <AssignmentsTable
          assignments={assignments}
          tiers={tiers}
          onUnassign={(partnerId, partnerName) =>
            setUnassignTarget({ partnerId, partnerName })
          }
        />
      )}

      <AssignPartnerDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        programId={programId}
        tiers={tiers}
        assignments={assignments}
      />

      <Dialog
        open={unassignTarget !== null}
        onOpenChange={(o) => !o && setUnassignTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove assignment?</DialogTitle>
            <DialogDescription>
              Remove{" "}
              <span className="font-medium text-foreground">
                {unassignTarget?.partnerName}
              </span>{" "}
              from this tier program. The partner can be re-assigned later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setUnassignTarget(null)}
              disabled={unassign.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmUnassign}
              disabled={unassign.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {unassign.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignmentsTable({
  assignments,
  tiers,
  onUnassign,
}: {
  assignments: AdminTierAssignmentRow[];
  tiers: Tier[];
  onUnassign: (partnerId: string, partnerName: string) => void;
}) {
  const tierById = useMemo(() => {
    const m = new Map<string, Tier>();
    for (const t of tiers) m.set(t.id, t);
    return m;
  }, [tiers]);

  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Partner</th>
            <th className="px-4 py-2.5">Tier</th>
            <th className="px-4 py-2.5 text-right">Assigned</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-border/70">
          {assignments.map((a) => {
            const tier = tierById.get(a.tier_id);
            return (
              <tr key={a.id} className="group transition-colors hover:bg-muted/30">
                <td className="px-4 py-3 font-medium">{a.partner_name}</td>
                <td className="px-4 py-3">
                  {tier ? (
                    <TierBadge
                      assignment={{
                        tierName: tier.name,
                        badgePath: tier.badge_path,
                        colorHex: tier.color_hex,
                        legacyTier: tier.legacy_tier,
                      }}
                    />
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {a.tier_name}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                  {formatRelative(a.assigned_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => onUnassign(a.partner_id, a.partner_name)}
                    aria-label={`Remove ${a.partner_name} from this program`}
                    className="rounded-md p-1.5 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AssignPartnerDialog({
  open,
  onOpenChange,
  programId,
  tiers,
  assignments,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  programId: string;
  tiers: Tier[];
  assignments: AdminTierAssignmentRow[];
}) {
  const partnersQuery = usePartners();
  const [partnerId, setPartnerId] = useState("");
  const [tierId, setTierId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const assign = useAssignPartnerTier();

  const assignedIds = useMemo(
    () => new Set(assignments.map((a) => a.partner_id)),
    [assignments],
  );

  const candidates = useMemo(
    () => (partnersQuery.data ?? []).filter((p) => !assignedIds.has(p.id)),
    [partnersQuery.data, assignedIds],
  );

  const pickedPartner = useMemo(
    () => candidates.find((p) => p.id === partnerId),
    [candidates, partnerId],
  );

  function reset() {
    setPartnerId("");
    setTierId("");
    setPickerOpen(false);
  }

  function onChangeOpen(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function onSubmit() {
    if (!partnerId || !tierId) {
      toast.error("Pick a partner and a tier");
      return;
    }
    const pickedTier = tiers.find((t) => t.id === tierId);
    assign.mutate(
      {
        partner_id: partnerId,
        program_id: programId,
        tier_id: tierId,
        patchPartnerLegacyTier: pickedTier?.legacy_tier ?? undefined,
      },
      {
        onSuccess: () => {
          toast.success("Partner assigned");
          reset();
          onOpenChange(false);
        },
        onError: (err) => {
          toast.error(formatApiError(err, "Failed to assign partner"));
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onChangeOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Assign partner
          </DialogTitle>
          <DialogDescription>
            Pick a partner and the tier to assign them. The partners list is
            limited to those not already in this program.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Partner</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={pickerOpen}
                  className="w-full justify-between font-normal"
                  disabled={partnersQuery.isLoading}
                >
                  {pickedPartner ? (
                    <span className="truncate text-left">
                      {pickedPartner.company_name}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">
                      {partnersQuery.isLoading
                        ? "Loading partners…"
                        : "Search partners…"}
                    </span>
                  )}
                  <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by company name…" />
                  <CommandList>
                    <CommandEmpty>No unassigned partners match.</CommandEmpty>
                    <CommandGroup>
                      {candidates.map((p) => (
                        <CommandItem
                          key={p.id}
                          value={p.company_name}
                          onSelect={() => {
                            setPartnerId(p.id);
                            setPickerOpen(false);
                          }}
                        >
                          <span className="truncate">{p.company_name}</span>
                          {partnerId === p.id && (
                            <Check
                              className={cn("ml-auto h-3.5 w-3.5 opacity-100")}
                            />
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ap-tier">Tier</Label>
            <Select value={tierId} onValueChange={setTierId}>
              <SelectTrigger id="ap-tier">
                <SelectValue placeholder="Pick a tier" />
              </SelectTrigger>
              <SelectContent>
                {tiers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onChangeOpen(false)}
            disabled={assign.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={assign.isPending || !partnerId || !tierId}
          >
            {assign.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
